/**
 * Per-request outcome log for the headless PDF print page.
 *
 * Widget failures inside Chromium are otherwise invisible to the Node process that ran Playwright:
 * the PDF just arrives with holes. `CsrfMiddleware` records outcomes here and
 * `run-security-dashboard-pdf.ts` reads the log off `window` after readiness, so a failed export
 * can be diagnosed from server logs alone (status, Aegis error code, request id, token TTL).
 */

import { accessTokenTtlSeconds } from '@/lib/access-token-expiry'
import { isSecurityDashboardPdfPrintBrowserContext } from '@/lib/security-dashboard-pdf-injected-snapshot'

export const PDF_NETWORK_LOG_WINDOW_KEY = '__pdfNetworkLog'

/** Bound the log: a stuck render must not grow the heap without limit. */
export const PDF_NETWORK_LOG_MAX_ENTRIES = 250

export type PdfApiOutcome = {
  /** ms since epoch when the outcome was recorded. */
  at: number
  method: string
  /** Path only — query strings can carry tenant/case identifiers. */
  path: string
  status: number | null
  /** Aegis `errorCode` from the response body, when present. */
  errorCode?: string
  requestId?: string
  /** Remaining access token lifetime in seconds; negative means the request used a dead token. */
  tokenTtlSec: number | null
  note?: string
}

type PdfNetworkLogWindow = typeof globalThis & {
  [PDF_NETWORK_LOG_WINDOW_KEY]?: PdfApiOutcome[]
}

export function isPdfPrintTelemetryContext(): boolean {
  return isSecurityDashboardPdfPrintBrowserContext()
}

function pathFromUrl(urlStr: string): string {
  try {
    const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://placeholder.local'
    return new URL(urlStr, base).pathname
  } catch {
    return urlStr.split('?')[0] ?? urlStr
  }
}

function currentTokenTtlSec(): number | null {
  try {
    const raw =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { access_token?: string }
    return accessTokenTtlSeconds(parsed?.access_token)
  } catch {
    return null
  }
}

/** Aegis error codes live in the JSON body; only read it for failures to keep the happy path cheap. */
async function readErrorCode(response: Response | undefined): Promise<string | undefined> {
  if (!response) return undefined
  try {
    const body = (await response.clone().json().catch(() => null)) as
      | { errorCode?: unknown; error?: unknown; message?: unknown }
      | null
    if (!body) return undefined
    for (const candidate of [body.errorCode, body.error]) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim().slice(0, 80)
    }
    if (typeof body.message === 'string' && body.message.trim()) return body.message.trim().slice(0, 120)
    return undefined
  } catch {
    return undefined
  }
}

export function appendPdfApiOutcome(entry: PdfApiOutcome): void {
  if (typeof window === 'undefined') return
  try {
    const w = window as PdfNetworkLogWindow
    const log = w[PDF_NETWORK_LOG_WINDOW_KEY] ?? []
    if (log.length >= PDF_NETWORK_LOG_MAX_ENTRIES) {
      log.shift()
    }
    log.push(entry)
    w[PDF_NETWORK_LOG_WINDOW_KEY] = log
  } catch {
    /* telemetry must never break a render */
  }
}

/**
 * Record an API outcome when running inside the PDF print page. No-op elsewhere, so the live
 * dashboard pays nothing for it.
 */
export async function recordPdfApiOutcome(input: {
  url: string
  method?: string
  status: number | null
  response?: Response
  note?: string
}): Promise<void> {
  if (!isPdfPrintTelemetryContext()) return
  const failed = input.status == null || input.status >= 400
  const entry: PdfApiOutcome = {
    at: Date.now(),
    method: (input.method || 'GET').toUpperCase(),
    path: pathFromUrl(input.url),
    status: input.status,
    tokenTtlSec: currentTokenTtlSec(),
  }
  if (input.note) entry.note = input.note
  if (failed) {
    const errorCode = await readErrorCode(input.response)
    if (errorCode) entry.errorCode = errorCode
    const requestId =
      input.response?.headers?.get('x-request-id') ?? input.response?.headers?.get('x-correlation-id') ?? null
    if (requestId) entry.requestId = requestId
  }
  appendPdfApiOutcome(entry)
}

export function readPdfNetworkLog(): PdfApiOutcome[] {
  if (typeof window === 'undefined') return []
  try {
    return [...((window as PdfNetworkLogWindow)[PDF_NETWORK_LOG_WINDOW_KEY] ?? [])]
  } catch {
    return []
  }
}

export type PdfNetworkLogSummary = {
  total: number
  failed: number
  byStatus: Record<string, number>
  /** Requests that were issued with an already-expired token. */
  expiredTokenRequests: number
  minTokenTtlSec: number | null
  failures: PdfApiOutcome[]
}

export function summarizePdfNetworkLog(entries: PdfApiOutcome[]): PdfNetworkLogSummary {
  const byStatus: Record<string, number> = {}
  let failed = 0
  let expiredTokenRequests = 0
  let minTokenTtlSec: number | null = null
  const failures: PdfApiOutcome[] = []

  for (const e of entries) {
    const key = e.status == null ? 'network-error' : String(e.status)
    byStatus[key] = (byStatus[key] ?? 0) + 1
    if (e.status == null || e.status >= 400) {
      failed += 1
      if (failures.length < 25) failures.push(e)
    }
    if (e.tokenTtlSec != null) {
      if (e.tokenTtlSec <= 0) expiredTokenRequests += 1
      if (minTokenTtlSec == null || e.tokenTtlSec < minTokenTtlSec) minTokenTtlSec = e.tokenTtlSec
    }
  }

  return { total: entries.length, failed, byStatus, expiredTokenRequests, minTokenTtlSec, failures }
}
